import React, { useState, useRef, useEffect } from 'react';
import { translations } from '../../translations';

interface FollowerLineChartProps {
  groupedHistory: Array<{
    date: string;
    count: number;
    gains: number;
  }>;
  isDarkMode: boolean;
  language: string;
}

export function FollowerLineChart({ groupedHistory, isDarkMode, language }: FollowerLineChartProps) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: string } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    const updateDimensions = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const formatNumber = (num: number) => {
    if (Math.abs(num) >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + 'M';
    }
    if (Math.abs(num) >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + 'K';
    }
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(language === 'FR' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: '2-digit'
    }).format(date);
  };

  const { minCount, maxCount, yAxisLabels } = React.useMemo(() => {
    if (!groupedHistory.length) {
      return { minCount: 0, maxCount: 100, yAxisLabels: [0, 25, 50, 75, 100] };
    }

    const counts = groupedHistory.map(entry => entry.count);
    const min = Math.min(...counts);
    const max = Math.max(...counts);
    const range = max - min;
    
    const padding = Math.ceil(range * 0.2);
    const adjustedMin = Math.max(0, Math.floor(min - padding));
    const adjustedMax = Math.ceil(max + padding);
    
    const step = Math.ceil((adjustedMax - adjustedMin) / 4);
    const labels = Array.from({ length: 5 }, (_, i) => 
      Math.round(adjustedMin + (step * i))
    );
    
    return { minCount: adjustedMin, maxCount: adjustedMax, yAxisLabels: labels };
  }, [groupedHistory]);

  const generateSmoothPath = (points: { x: number, y: number }[]): string => {
    if (points.length < 2) return '';

    const path = [];
    path.push(`M ${points[0].x} ${points[0].y}`);

    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      
      const controlX1 = current.x + (next.x - current.x) / 3;
      const controlX2 = current.x + 2 * (next.x - current.x) / 3;
      
      path.push(`C ${controlX1} ${current.y}, ${controlX2} ${next.y}, ${next.x} ${next.y}`);
    }

    return path.join(' ');
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!groupedHistory.length || !dimensions.width) return;

    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    const segmentWidth = dimensions.width / (groupedHistory.length - 1);
    const index = Math.round(x / segmentWidth);
    
    if (index >= 0 && index < groupedHistory.length) {
      const entry = groupedHistory[index];
      const xPos = index * segmentWidth;
      const yPos = dimensions.height - (((entry.count - minCount) / (maxCount - minCount)) * dimensions.height);

      setTooltip({
        x: xPos,
        y: yPos,
        content: `${formatDate(entry.date)}: ${entry.count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const points = React.useMemo(() => {
    if (!dimensions.width || !dimensions.height || !groupedHistory.length) return [];

    return groupedHistory.map((entry, i) => ({
      x: (i / (groupedHistory.length - 1)) * dimensions.width,
      y: dimensions.height - (((entry.count - minCount) / (maxCount - minCount)) * dimensions.height)
    }));
  }, [groupedHistory, dimensions, minCount, maxCount]);

  if (!groupedHistory.length) {
    return (
      <div className="h-[200px] flex items-center justify-center">
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t.no_data_available}
        </p>
      </div>
    );
  }

  return (
    <div className="flex">
      <div className="pr-4 flex flex-col justify-between text-[10px]">
        {[...yAxisLabels].reverse().map((value, index) => (
          <div
            key={index}
            className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}
          >
            {formatNumber(value)}
          </div>
        ))}
      </div>

      <div className="flex-1 min-w-0">
        <div className="relative h-[200px]">
          <div className="absolute inset-0">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={`h-${i}`}
                className={`absolute w-full border-t ${
                  isDarkMode ? 'border-gray-800' : 'border-gray-100'
                }`}
                style={{ top: `${(i * 25)}%` }}
              />
            ))}
            
            {groupedHistory.map((_, i) => (
              <div
                key={`v-${i}`}
                className={`absolute h-full border-l ${
                  isDarkMode ? 'border-gray-800' : 'border-gray-100'
                }`}
                style={{ left: `${(i / (groupedHistory.length - 1)) * 100}%` }}
              />
            ))}
          </div>

          <svg
            ref={svgRef}
            className="absolute inset-0 w-full h-full overflow-visible"
            preserveAspectRatio="none"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {points.length > 1 && (
              <path
                d={generateSmoothPath(points)}
                fill="none"
                stroke="rgb(249, 115, 22)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {tooltip && (
              <g transform={`translate(${tooltip.x},${tooltip.y})`}>
                <circle
                  r="4"
                  fill="rgb(249, 115, 22)"
                  stroke="white"
                  strokeWidth="2"
                />
                <foreignObject
                  x="-60"
                  y="-40"
                  width="120"
                  height="30"
                  style={{ overflow: 'visible' }}
                >
                  <div className={`px-2 py-1 text-xs rounded shadow-lg text-center ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border border-[#3b3b3b]'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}>
                    {tooltip.content}
                  </div>
                </foreignObject>
              </g>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}